import React, { forwardRef, memo, useImperativeHandle } from 'react';
import { TextArea } from '../TextAreaComponent/TextArea';
// no direct User usage in this file
import { classNames } from '../../utils/classNames';
import type { TextAreaWithSuggestionsProps } from './TextAreaWithSuggestions.types';
import { MentionPopup } from '../CommonTooltip';
import { MentionPopupLayer } from './MentionPopupLayer';
import { InputActionBar } from '../InputActionBar/InputActionBar';
import { DEFAULT_MAX_COMMENT_LENGTH } from '../../constants/commentLimits';
import './TextAreaWithSuggestions.css';
import { useTextAreaWithSuggestions } from './useTextAreaWithSuggestions';

const TextAreaWithSuggestionsComponent = forwardRef<
  HTMLTextAreaElement,
  TextAreaWithSuggestionsProps
>(
  (
    {
      enableMentions = true,
      enableHyperlinks = true,

      usersApiEndpoint,
      onMentionsChange,
      onHyperlinksChange,
      onSubmit,
      mentionPopupStyle,
      mentionPopupDisplay = 'name',

      MentionPopup: CustomMentionPopup = MentionPopup,
      debounceDelay = 300,
      maxSuggestions = 10,
      maxAllowedCharacters,
      value = '',
      onChange,
      onKeyDown,
      onBlur,
      className = '',
      children,
      actionBarConfig,
      registerRefocusInput,
      ...props
    },
    ref
  ) => {
    const maxLength = maxAllowedCharacters ?? DEFAULT_MAX_COMMENT_LENGTH;
    const {
      textareaRef,
      popupState,
      users,
      loading,
      mentions,
      selectedIndex,
      handleChange,
      handleKeyDownEvent,
      handleBlur,
      handleMentionSelect,
      closePopup,
      hasActionBar,
      effectiveOnSendClick,
      handlePlusMenuSelect,
      actionBarConfigForBar
    } = useTextAreaWithSuggestions({
      value,
      enableMentions,
      enableHyperlinks,
      usersApiEndpoint,
      debounceDelay,
      maxSuggestions,
      onChange,
      onKeyDown,
      onBlur,
      onSubmit,
      onMentionsChange,
      onHyperlinksChange,
      actionBarConfig,
      registerRefocusInput
    });

    // Forward ref to actual textarea
    useImperativeHandle(ref, () => textareaRef.current!, []);

    const textareaClassName = classNames(
      'textarea-with-suggestions__textarea',
      mentions.length > 0 &&
        'textarea-with-suggestions__textarea--with-mentions'
    );

    return (
      <div className={classNames('textarea-with-suggestions', className)}>
        <div className="textarea-with-suggestions__textarea-container">
          <TextArea
            {...props}
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDownEvent}
            onSubmit={onSubmit}
            onBlur={handleBlur}
            autoComplete="off"
            className={textareaClassName}
            maxLength={maxLength}
          >
            {children}
          </TextArea>
        </div>

        <MentionPopupLayer
          enableMentions={enableMentions}
          isOpen={popupState.isOpen}
          users={users}
          loading={loading}
          selectedIndex={selectedIndex}
          position={popupState.position}
          query={popupState.query}
          mentionPopupStyle={mentionPopupStyle}
          mentionPopupDisplay={mentionPopupDisplay}
          CustomMentionPopup={CustomMentionPopup}
          onSelect={handleMentionSelect}
          onClose={closePopup}
        />

        {hasActionBar && actionBarConfigForBar && (
          <InputActionBar
            {...actionBarConfigForBar}
            onPlusMenuSelect={handlePlusMenuSelect}
            onSendClick={effectiveOnSendClick}
          />
        )}
      </div>
    );
  }
);

TextAreaWithSuggestionsComponent.displayName = 'TextAreaWithSuggestions';

export const TextAreaWithSuggestions = memo(TextAreaWithSuggestionsComponent);

export default TextAreaWithSuggestions;
